import globalLogger from "@lazarentertainment/js-logger"
import { useRef, useState } from "react"

/**
 * React hook to provide a copy-to-clipboard function
 * Also provides a state boolean which is set to true for a temporary duration,
 * whenever the copy-to-clipboard function is called.
 *
 * @param {number} [millisecondsToDisplayNotification=1200]
 *  duration that shouldShowCopiedNotification will be true
 * @return {[function, boolean]} an array of:
 * {function} handleCopyToClipboard(stringToCopy) -
 *  call this to begin copying to clipboard & showing notification
 * {boolean} shouldShowCopiedNotification -
 *  whether to show a notification that something was copied to clipboard
 */

export default function useCopyToClipboardWithNotification(
  millisecondsToDisplayNotification = 1200,
): [(stringToCopy: string) => void, boolean] {
  const [shouldShowCopiedNotification, setShouldShowCopiedNotification] =
    useState(false)
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  function handleCopyToClipboard(stringToCopy: string) {
    navigator.clipboard
      .writeText(stringToCopy)
      .then(() => {
        if (timeout.current) {
          clearTimeout(timeout.current)
        }
        setShouldShowCopiedNotification(true)
        timeout.current = setTimeout(() => {
          setShouldShowCopiedNotification(false)
        }, millisecondsToDisplayNotification)
      })
      .catch(() => {
        globalLogger.error("Copy to clipboard failed!")
      })
  }

  return [handleCopyToClipboard, shouldShowCopiedNotification]
}
